/**
 * WorkspaceEventContext — Provides the event bus and accumulator to all
 * workspace components via React context.
 *
 * Components use:
 *   const { emit } = useWorkspaceEvents()
 *   emit({ type: 'code_edit', ... })
 *
 * Workspace.tsx uses:
 *   const { drain } = useWorkspaceEvents()
 *   const deltas = drain() // before sending agent request
 */

import { createContext, useContext, useRef, useEffect, type ReactNode } from 'react'
import { WorkspaceEventBus, type WorkspaceEvent } from '../lib/workspace-event-bus'
import { ContextAccumulator, type ContextDelta } from '../lib/context-accumulator'

interface WorkspaceEventContextValue {
  emit: (event: WorkspaceEvent) => void
  drain: () => ContextDelta[]
  hasPending: boolean
}

const Context = createContext<WorkspaceEventContextValue | null>(null)

export function WorkspaceEventProvider({ children }: { children: ReactNode }) {
  const busRef = useRef(new WorkspaceEventBus())
  const accumulatorRef = useRef(new ContextAccumulator())

  useEffect(() => {
    accumulatorRef.current.attach(busRef.current)
    return () => {
      accumulatorRef.current.detach()
      busRef.current.clear()
    }
  }, [])

  const value: WorkspaceEventContextValue = {
    emit: (event) => busRef.current.emit(event),
    drain: () => accumulatorRef.current.drain(),
    get hasPending() { return accumulatorRef.current.hasPending },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useWorkspaceEvents(): WorkspaceEventContextValue {
  const ctx = useContext(Context)
  if (!ctx) {
    // Return a no-op fallback when used outside the provider
    // (e.g., in storybook, tests, or components mounted before workspace)
    return {
      emit: () => {},
      drain: () => [],
      hasPending: false,
    }
  }
  return ctx
}
